import React, { useState, useRef, useEffect } from 'react';

/**
 * LazyImage - 遅延読み込み対応の画像コンポーネント
 * CORBエラーやネットワークエラーに対する堅牢なフォールバック機能付き
 */
const LazyImage = ({
  src,
  alt,
  className = '',
  fallbackSrc = null,
  placeholder = null,
  crossOrigin = 'anonymous',
  loading = 'lazy',
  onLoad = () => { },
  onError = () => { },
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(placeholder || createPlaceholderSVG(alt));
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // プレースホルダーSVGを生成
  function createPlaceholderSVG(altText = '画像', width = 400, height = 300) {
    const svg = `
      <svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${width} ${height}'>
        <rect width='${width}' height='${height}' fill='#f3f4f6'/>
        <text x='50%' y='50%' text-anchor='middle' dy='.3em' font-family='Arial, sans-serif' font-size='14' fill='#6b7280'>
          ${altText}を読み込み中...
        </text>
      </svg>
    `;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }

  // エラー用のSVGを生成
  function createErrorSVG(altText = '画像', width = 400, height = 300) {
    const svg = `
      <svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${width} ${height}'>
        <rect width='${width}' height='${height}' fill='#fef2f2'/>
        <text x='50%' y='50%' text-anchor='middle' dy='.3em' font-family='Arial, sans-serif' font-size='12' fill='#dc2626'>
          ${altText}の読み込みに失敗しました
        </text>
      </svg>
    `;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }

  // Intersection Observerによる遅延読み込み
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && src && imageSrc !== src) {
            setImageSrc(src);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, [src, imageSrc]);

  const handleLoad = (e) => {
    setIsLoading(false);
    setHasError(false);
    onLoad(e);
  };

  const handleError = (e) => {
    setIsLoading(false);
    setHasError(true);

    // フォールバック画像を試す
    if (fallbackSrc && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      return;
    }

    // エラー用SVGに切り替え
    e.target.src = createErrorSVG(alt);
    e.target.onerror = null; // 無限ループを防ぐ

    onError(e);
  };

  return (
    <img
      ref={imgRef}
      src={imageSrc}
      alt={alt}
      className={`transition-opacity duration-300 ${isLoading ? 'opacity-50' : 'opacity-100'} ${className}`}
      crossOrigin={crossOrigin}
      loading={loading}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    />
  );
};

export default LazyImage;
